import { FormLeavingListener } from '@exam-management/components';
import {
  QuestionGroupType,
  useCreateQuestionMutation,
  type QuestionInputData,
} from '@exam-management/graphql';
import {
  DEFAULT_FORM_VALUES,
  type QContentCreateFormContextData,
} from '@exam-management/qcontent-creation';
import {
  QContentDetailsSection,
  QContentLocationSection,
  QuestionLayout,
} from '@exam-management/qcontent-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate, useParams } from '@exam-management/router';
import { Form } from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

export default function QContentCreatePage() {
  const { structureId } = useParams('/qbank-structures/:structureId/qcontents/single/:contentId');

  const formContext = useFormContext<QContentCreateFormContextData>();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const logger = useLogger();

  const [createQuestion] = useCreateQuestionMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'Question has been saved',
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const handleGoToEdit = (contentId: string) => {
    navigate('/qbank-structures/:structureId/qcontents/single/:contentId', {
      params: {
        structureId,
        contentId,
      },
    });
  };

  const handleSave = async (data: QContentCreateFormContextData) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;

    if (!qBankStructureLevelItemId) return null;

    const isCalculated =
      data.information.calculated === '' ? null : data.information.calculated === 'true';
    const pValue =
      data.information.pValue.selected === 'pInitial'
        ? data.information.pValue.pInitial
        : data.information.pValue.pStat;

    const group =
      data.location.LOQ?.selected === 'true'
        ? {
            name: data.location.LOQ.value?.value as string,
            type: QuestionGroupType.LotOfQuestion,
          }
        : {
            type: QuestionGroupType.Single,
          };
    const cognitiveDomains = data.properties.cognitiveDomain.map((domain) => domain.value);

    const convertedData: QuestionInputData = {
      details: parseContentForApi(data.detail.details),
      choices: data.detail.choices.map((choice) => ({
        ...choice,
        label: parseContentForApi(choice.label),
      })),
      correctAnswer: data.detail.correctAnswer,
      calculated: isCalculated,
      pValue: pValue !== '' ? pValue : undefined,
      bIndexValue: data.information.bIndex !== '' ? data.information.bIndex : undefined,
      cognitiveDomains,
      learningObjectiveStatement: data.properties.learningObjectiveStatement?.value ?? undefined,
      solution: parseContentForApi(data.properties.solution),
      reference: parseContentForApi(data.properties.reference),
      group,
      ...data.owners,
    };

    const result = await createQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: structureId,
          data: convertedData,
        },
      },
    });

    return result.data?.qctCreateQuestion.id;
  };

  const handleClickSave = async (data: unknown) => {
    const contentId = await handleSave(data as QContentCreateFormContextData);
    if (contentId) {
      handleGoToEdit(contentId);
      return Promise.resolve();
    } else {
      logger.error('Failed to save content');
      return Promise.reject(new Error('Failed to save content'));
    }
  };

  const handleClickSaveAndCreateNew = async (data: unknown) => {
    const contentId = await handleSave(data as QContentCreateFormContextData);
    if (contentId) {
      return Promise.resolve();
    } else {
      logger.error('Failed to save content');
      return Promise.reject(new Error('Failed to save content'));
    }
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      const locationValues = formContext.getValues('location');
      formContext.reset(
        {
          ...DEFAULT_FORM_VALUES,
          location: locationValues,
        },
        {
          keepDefaultValues: true,
        },
      );
    }
  }, [formContext.formState.isSubmitSuccessful]);

  return (
    <Form>
      <QuestionLayout sideContent={<QContentLocationSection refQBankStructureId={structureId} />}>
        <QContentDetailsSection
          onSubmit={handleClickSave}
          onSubmitAndCreateNew={handleClickSaveAndCreateNew}
        />
      </QuestionLayout>
      <FormLeavingListener onSubmit={handleSave} />
    </Form>
  );
}
